import React, { useState } from 'react';
import classNames from 'classnames';
import ClickOutside from '../ClickOutside';
import { callAll } from '../internals/utils';

export type HoverTooltipPositions =
  | 'top'
  | 'top-right'
  | 'top-left'
  | 'bottom'
  | 'right'
  | 'left';

export interface HoverTooltipProps extends React.HTMLAttributes<HTMLElement> {
  content?: string;
  render?: (setIsClicked: any) => React.ReactNode;
  position?: HoverTooltipPositions;
  showOnClick?: boolean;
  noHover?: boolean;
  children: any;
}

const styles: any = {
  tooltipContainer: {
    position: 'relative',
    width: '100%'
  },
  top: {
    bottom: '100%',
    left: '50%',
    transform: 'translate(-50%, 0)'
  },
  'top-right': {
    bottom: '100%'
  },
  'top-left': {
    bottom: '100%'
  },
  bottom: {
    top: '100%',
    left: '50%',
    transform: 'translate(-50%, 0)'
  },
  right: {
    left: '100%',
    top: '50%',
    transform: 'translate(0, -50%)'
  },
  left: {
    right: '100%',
    top: '50%',
    transform: 'translate(0, -50%)'
  }
};

const HoverTooltip: React.FunctionComponent<HoverTooltipProps> = props => {
  const {
    content,
    children,
    position = 'top',
    showOnClick = false,
    noHover,
    render
  } = props;

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const handleClickOutside = () => {
    if (isClicked) setIsClicked(false);
  };

  const isOpen = () => {
    if (!content && !props.render) return false;
    if (noHover) return isFocused || isClicked;
    return isFocused || isClicked || isHovering;
  };

  const wrapChildrenProps = (childrenProps, showOnClickBool) => {
    if (showOnClickBool) {
      return {
        ...childrenProps,
        onClick: callAll(childrenProps.onClick, () => setIsClicked(true))
      };
    }
    return {
      ...childrenProps,
      onMouseOver: callAll(childrenProps.onMouseOver, () =>
        setIsHovering(true)
      ),
      onMouseLeave: callAll(childrenProps.onMouseLeave, () =>
        setIsHovering(false)
      ),
      onFocus: callAll(childrenProps.onFocus, () => setIsFocused(true)),
      onBlur: callAll(childrenProps.onBlur, () => setIsFocused(false))
    };
  };

  return (
    <ClickOutside handleClickOutside={handleClickOutside}>
      <div style={{ ...styles.tooltipContainer }}>
        <div
          className={classNames({
            'hover-tooltip': true,
            in: isOpen(),
            [position]: true
          })}
          role='tooltip'
          style={{
            ...styles[position],
            display: isOpen() ? 'block' : 'none'
          }}
        >
          <div className='tooltip-arrow' />
          <div className='tooltip-inner' style={{ maxWidth: 'auto' }}>
            {content || (render && render(() => setIsClicked(false)))}
          </div>
        </div>
        {children &&
          React.cloneElement(
            children as React.ReactElement,
            wrapChildrenProps(children.props, showOnClick)
          )}
      </div>
    </ClickOutside>
  );
};

export default HoverTooltip;
